import { ICONS, ST_CLR, PR_CLR, PJ_NODOS, colorRama } from "../../constants";
import { nodeBox, geoDe, puertosDe, portPoint, salidasDe } from "./flowGeometry";

// Los iconos dentro del SVG NO se pintan con <Icon/>: ese componente emite su
// propio <svg width height> en pixeles, y dentro de un viewBox escalado se
// comporta distinto en cada motor. Aqui se usa el path directamente, escalado
// desde su rejilla original de 16x16.
//
// El separador " M " es la convencion de la tabla ICONS para los iconos de
// varios trazos; hay que respetarla o se pierde todo menos el primero.
function PathIcon({ name, x, y, size = 13, color }) {
  const p = ICONS[name];
  if (!p) return null;
  return (
    <g transform={`translate(${x},${y}) scale(${size / 16})`} fill={color} pointerEvents="none">
      {p.split(" M ").map((seg, i) => <path key={i} d={i === 0 ? seg : "M " + seg} />)}
    </g>
  );
}

// Texto recortado a mano: en SVG no hay text-overflow, asi que se corta por
// numero de caracteres estimando el ancho medio de DM Sans a este tamaño.
const recorta = (s, max) => {
  const t = String(s || "");
  return t.length > max ? t.slice(0, max - 1) + "…" : t;
};

// Los iconos salen de PJ_NODOS y no de una tabla propia: son los mismos con los
// que se crea cada nodo desde la paleta, y tenerlos escritos dos veces era pedir
// que un dia el icono del menu y el del lienzo dejaran de coincidir.
//
// Un paso no entra: su forma ya es la de un paso y el icono seria ruido.
const ICONO_KIND = Object.fromEntries(
  PJ_NODOS.filter(n => n.key !== "tarea").map(n => [n.key, n.icon])
);

export default function FlowNode({
  n, resumen, seleccionado, atenuado, enCurso, editable, minutos,
  onPointerDown, onPointerDownPuerto, onDobleClic, puertoCaliente,
}) {
  const b = nodeBox(n);
  const g = geoDe(n);
  const kind = n.kind || "tarea";
  const esTrabajo = kind === "tarea";
  // El color del nodo. Para un paso, el de su estado.
  //
  // El INICIO es la excepcion: no tiene trabajo propio —su estado guardado se
  // queda en pendiente para siempre— y a cambio lleva el resumen de TODO el
  // proyecto. Es la puerta del flujo, el sitio donde se mira primero, y asi un
  // vistazo al circulo contesta "¿como va esto?" sin recorrer nada.
  const clr = resumen ? resumen.color : (ST_CLR[n.status] || "#6b7280");

  // El color del inicio se explica con palabras. Un color sin nombre no lo lee
  // quien no distinga bien los tonos, y aqui ademas resume un dato que no esta
  // escrito en ninguna otra parte de la pantalla.
  const resumenTexto = resumen && (resumen.clave === "vacio" ? resumen.label : [
    resumen.label,
    `${resumen.total} ${resumen.total === 1 ? "paso" : "pasos"}`,
    ...Object.entries(resumen.conteo)
      .sort((a, b) => b[1] - a[1])
      .map(([e, n_]) => `${n_} ${e.toLowerCase()}`),
  ].join(" · "));
  const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
  const puertos = puertosDe(n);
  const salidas = salidasDe(n);

  // Bloqueada tambien se distingue por el trazo, no solo por el color: en una
  // captura en blanco y negro un rojo no se diferencia de "prioridad alta".
  const bloqueada = n.status === "Bloqueada";
  const trazo = bloqueada ? "5 3" : undefined;

  const est  = Number(n.estimacion_min) || 0;
  const real = Number(minutos) || 0;
  // Sin estimacion no hay medida, asi que el relleno lo dice el estado y nada
  // mas: hubo un porcentaje a mano en cada paso y se quito, porque era el unico
  // numero del proyecto que nadie mantenia al dia.
  const avance = est ? Math.min(1, real / est) : (n.status === "Completada" ? 1 : 0);
  const exceso = est && real > est ? Math.min(1, (real - est) / est) : 0;

  const clases = ["df-node"];
  if (enCurso)  clases.push("df-busy");
  if (atenuado) clases.push("df-dim");

  // ── Forma ───────────────────────────────────────────────────────────────────
  // Una sola definicion por tipo, que sirve para el cuerpo Y para el contorno de
  // seleccion. `crece` infla la figura hacia fuera manteniendo el centro.
  //
  // El contorno TIENE que seguir la forma: cuando era un rectangulo sobre la
  // caja envolvente, en un condicional salia un cuadrado enorme alrededor de un
  // rombo pequeño —la caja de un rombo tiene casi el doble de area que la
  // figura— y ese recuadro pesaba mucho mas de lo que decia.
  const forma = (crece = 0, props = {}) => {
    if (kind === "condicional") {
      // Al inflar un rombo por su caja, el borde solo se aleja crece/√2. Se
      // compensa para que el hueco visible sea el que se pide.
      const d = crece * Math.SQRT2;
      return (
        <polygon points={[
          `${cx},${b.y - d}`, `${b.x + b.w + d},${cy}`,
          `${cx},${b.y + b.h + d}`, `${b.x - d},${cy}`,
        ].join(" ")} {...props} />
      );
    }
    if (kind === "hito") {
      const lado = 24 + crece * 2;
      return (
        <rect x={cx - lado / 2} y={cy - lado / 2} width={lado} height={lado}
          rx={3} transform={`rotate(45 ${cx} ${cy})`} {...props} />
      );
    }
    // El radio de un inicio o un fin es la mitad de su lado CORTO. Hoy son
    // cuadrados y da lo mismo, pero la regla es la que hace que el circulo siga
    // saliendo redondo si algun dia cambia una de las dos medidas, sin tener que
    // confiar en que SVG recorte un radio mayor que el lado.
    const rx = kind === "inicio" || kind === "fin"
      ? Math.min(b.w, b.h) / 2 + crece
      : g.rx + crece;
    return (
      <rect x={b.x - crece} y={b.y - crece}
        width={b.w + crece * 2} height={b.h + crece * 2} rx={rx} {...props} />
    );
  };

  const relleno =
    kind === "hito" ? (PR_CLR[n.priority] || clr) + "33"
    : kind === "inicio" || kind === "fin" ? clr + "14"
    : "var(--surface)";
  const borde = kind === "hito" ? (PR_CLR[n.priority] || clr) : clr;

  // El <g> va SIN tabIndex a proposito. Con tabIndex={-1} el navegador enfocaba
  // el nodo al pulsarlo y pintaba su propio anillo de foco alrededor de la caja
  // envolvente del grupo —que incluye los circulos sensibles de los puertos,
  // r=12— asi que salia un recuadro negro y grueso sobresaliendo por los lados,
  // encima del contorno de acento que ya dice que esta seleccionado.
  //
  // Nada enfoca el nodo por codigo, y el teclado se maneja desde el <svg>
  // (flechas para mover la seleccion, Supr para borrar). Como no es enfocable,
  // no se anuncia como boton: el aria-label dice que es y en que estado esta.
  return (
    <g className={clases.join(" ")} role="img"
      aria-label={`${n.title}. ${resumenTexto || (esTrabajo ? n.status : kind)}`}
      onPointerDown={e => onPointerDown?.(e, n)}
      onDoubleClick={e => { e.stopPropagation(); onDobleClic?.(n); }}
      style={{ cursor: editable ? undefined : "pointer" }}>

      <title>
        {[n.title,
          resumenTexto || (esTrabajo ? n.status : `nodo de ${kind}`),
          (n.asignados || []).map(a => a.name).join(", ") || null,
          est ? `${(real / 60).toFixed(1)} h de ${(est / 60).toFixed(1)} h` : null,
          exceso > 0 ? `+${Math.round(((real / est) - 1) * 100)}% sobre lo estimado` : null,
          n.due_date ? `vence ${n.due_date}` : null,
        ].filter(Boolean).join(" · ")}
      </title>

      {/* Contorno de seleccion: la MISMA forma, por fuera y con trazo fino.
          Nunca filter:drop-shadow: con muchos nodos hunde el rendimiento en
          Firefox.
          Va RELLENO de acento translucido, no solo con el trazo, y separado
          6 px. El motivo: ST_CLR["En progreso"] es exactamente var(--accent),
          asi que en un paso en progreso el borde de estado y este contorno
          salian del mismo color y se leian como un doble borde. Con el halo,
          la seleccion se lee como una zona iluminada alrededor del nodo y no
          como otra linea, sea cual sea el color del estado. */}
      {/* El halo SI recibe el puntero, y eso arregla un fallo que se notaba al
          arrastrar: al estar seleccionado, el nodo se ve 6 px mas grande, asi
          que pinchabas en ese borde creyendo que era el nodo, fallabas el
          cuerpo, y el pointerdown seguia hasta el <svg> —donde vive el gesto de
          fondo—. Resultado: en vez de arrastrar el paso empezabas una
          marquesina, y al soltarla el rectangulo era diminuto, o sea un clic en
          el vacio, o sea DESELECCIONAR. Con pointerEvents:none el halo era una
          trampa de 6 px alrededor de todo lo que estuviera seleccionado.
          Los puertos se pintan DESPUES, asi que siguen ganando ellos donde se
          solapan. */}
      {seleccionado && forma(6, {
        fill: "var(--accent)", fillOpacity: 0.12,
        stroke: "var(--accent)", strokeWidth: 1.5,
        vectorEffect: "non-scaling-stroke",
      })}

      {/* Un inicio y un fin llevan trazo mas fino que el resto: son marcas de
          los extremos del flujo, no pasos, y con 1,5 pesaban igual que una
          tarea que si tiene algo que contar. */}
      {forma(0, { fill: relleno, stroke: borde,
        strokeWidth: kind === "hito" ? 2 : kind === "inicio" || kind === "fin" ? 1 : 1.5,
        strokeDasharray: trazo })}

      {/* Galon de prioridad: el mismo idioma visual que el borde superior de las
          tarjetas KPI. Solo en pasos de trabajo rectangulares. */}
      {esTrabajo && (
        <rect x={b.x} y={b.y + 8} width={4} height={b.h - 16} rx={2}
          fill={PR_CLR[n.priority] || "#9ca3af"} pointerEvents="none" />
      )}

      {/* Punto de estado. En las formas rectangulares va en su esquina; en un
          rombo esa esquina esta FUERA de la figura, asi que ahi no se pinta: el
          estado ya lo lleva el color del trazo. */}
      {esTrabajo && (
        <circle cx={b.x + 15} cy={b.y + 14} r={3.5} fill={clr} pointerEvents="none" />
      )}

      {/* Titulo.
          Cada forma lo pone donde de verdad hay sitio: en un rectangulo arriba
          a la izquierda, en un rombo en su banda central (la unica ancha), y en
          un hito debajo, porque dentro no cabe. Ponerlo siempre en la esquina de
          la caja envolvente era lo que hacia que el texto de un condicional
          apareciera flotando fuera del rombo. */}
      {kind === "hito" ? (
        <text className="df-txt" x={cx} y={b.y + b.h + 13} textAnchor="middle"
          style={{ fontSize: 11, fontWeight: 600 }}>
          {recorta(n.title, 24)}
        </text>
      ) : kind === "inicio" || kind === "fin" ? (
        // Un inicio y un fin NO llevan texto: solo el icono, que se pinta mas
        // abajo. El nombre sigue en el <title>, asi que quien lo necesite lo
        // tiene al pasar el raton; pintarlo era repetir el icono con letras.
        null
      ) : kind === "switch" ? (
        <text className="df-txt" x={b.x + 24} y={b.y + 20}
          style={{ fontSize: 12, fontWeight: 700 }}>
          {recorta(n.title, 20)}
        </text>
      ) : kind === "condicional" ? (
        <text className="df-txt" x={cx} y={cy + 4} textAnchor="middle"
          style={{ fontSize: 11.5, fontWeight: 700 }}>
          {recorta(n.title, 16)}
        </text>
      ) : (
        // Banda 1: el titulo, con el ancho entero del nodo. Los avatares ya no
        // le comen el final de la linea.
        <text className="df-txt" x={b.x + 24} y={b.y + 19}
          style={{ fontSize: 12, fontWeight: 700 }}>
          {recorta(n.title, 22)}
        </text>
      )}

      {/* Banda 2: tipo de trabajo y fecha, tambien con el ancho entero. */}
      {esTrabajo && (n.tipo || n.due_date) && (
        <text className="df-txt" x={b.x + 24} y={b.y + 37}
          style={{ fontSize: 9.5, fill: "var(--text3)" }}>
          {recorta([n.tipo, n.due_date].filter(Boolean).join(" · "), 28)}
        </text>
      )}

      {/* Icono del tipo de nodo. En un inicio y un fin es TODO el contenido y va
          centrado; en el rombo, centrado sobre el titulo; en las demas formas,
          en la esquina de abajo.

          El hito no lleva: su forma —un diamante— ya es su icono, y dentro de
          34 px con el nombre debajo no hay sitio para nada mas. */}
      {ICONO_KIND[kind] && kind !== "hito" && (
        kind === "inicio" || kind === "fin"
          ? <PathIcon name={ICONO_KIND[kind]} x={cx - 9} y={cy - 9} size={18} color={borde} />
          : kind === "condicional"
          ? <PathIcon name={ICONO_KIND[kind]} x={cx - 6} y={cy - 21} size={12} color={clr} />
          : <PathIcon name={ICONO_KIND[kind]} x={b.x + 8} y={b.y + b.h - 20} size={12} color={clr} />
      )}

      {/* Banda 3: los participantes, en su propia franja y no encima del
          titulo. Van a la derecha y crecen hacia la izquierda, asi que el
          cuarto y siguientes no empujan a nadie: se resumen con "+N". */}
      {esTrabajo && (n.asignados || []).slice(0, 3).map((a, i) => (
        <g key={a.user_id + a.rol} pointerEvents="none">
          <circle cx={b.x + b.w - 16 - i * 15} cy={b.y + 58} r={8}
            fill={(a.rol === "encargado" ? "#086C76" : "#9ca3af") + "26"}
            stroke={(a.rol === "encargado" ? "#086C76" : "#9ca3af") + "66"} strokeWidth={1} />
          <text x={b.x + b.w - 16 - i * 15} y={b.y + 61.5} textAnchor="middle"
            style={{ fontSize: 8.5, fontWeight: 700,
              fill: a.rol === "encargado" ? "#086C76" : "#9ca3af" }}>
            {String(a.name || "?")[0].toUpperCase()}
          </text>
        </g>
      ))}

      {/* Barra de avance. La referencia va DENTRO de la barra, igual que la
          rayita de las 8 h en HorasSemanales: asi no hay que cuadrar rotulos.

          El sobrecoste se decia antes con un trozo de barra que SOBRESALIA por
          la derecha del nodo. La intencion era buena —"se ha comido mas de lo que
          tenia"— pero cruzar el contorno se lee como un fallo de pintado y no
          como un mensaje, y ademas invadia el hueco por donde salen las flechas.
          En un Gantt eso funciona porque el eje es el tiempo y hay sitio a la
          derecha; dentro de una caja de 196 px no lo hay.

          Ahora se dice con el COLOR: pasarse tiñe la barra entera de rojo, que a
          la distancia a la que se mira un diagrama es lo unico que se distingue.
          Cuanto se ha pasado lo dice el <title>, que es donde cabe un numero. */}
      {esTrabajo && (
        <g pointerEvents="none">
          <rect x={b.x + 12} y={b.y + b.h - 11} width={b.w - 24} height={4} rx={2}
            fill="var(--bg)" />
          {avance > 0 && (
            <rect x={b.x + 12} y={b.y + b.h - 11} width={(b.w - 24) * avance} height={4} rx={2}
              fill={exceso > 0 ? "var(--red)" : clr} />
          )}
        </g>
      )}

      {/* Nota de bloqueo abreviada: si esta parado, lo importante es por que.
          Comparte banda con los participantes, que van a la derecha, asi que se
          recorta a 16 caracteres: con 28 se metia por debajo de los avatares,
          que es el mismo choque que tenia el titulo. */}
      {bloqueada && n.nota_bloqueo && (
        <text className="df-txt" x={b.x + 12} y={b.y + 62}
          style={{ fontSize: 9, fill: "var(--red)" }}>
          {recorta(n.nota_bloqueo, 16)}
        </text>
      )}

      {/* Nombres de las salidas de un switch, cada uno a la altura de su
          puerto. Sin esto el nodo tendria varios puertos identicos y no habria
          forma de saber cual es cual. */}
      {kind === "switch" && salidas.map((sal, i) => {
        const p = portPoint(n, sal.clave);
        const esResto = sal.clave === "resto";
        return (
          <g key={sal.clave} pointerEvents="none">
            <text className="df-txt" x={b.x + b.w - 14} y={p.y + 3.5} textAnchor="end"
              style={{ fontSize: 10, fontWeight: esResto ? 500 : 600,
                fill: esResto ? "var(--text3)" : "var(--text2)",
                fontStyle: esResto ? "italic" : "normal" }}>
              {recorta(sal.nombre, 20)}
            </text>
            {/* Linea de separacion entre salidas, muy tenue: agrupa el nombre
                con su puerto sin convertir el nodo en una tabla. */}
            {i > 0 && (
              <line x1={b.x + 12} y1={p.y - 11} x2={b.x + b.w - 12} y2={p.y - 11}
                stroke="var(--border2)" strokeWidth={1} />
            )}
          </g>
        );
      })}

      {/* ── Puertos ─────────────────────────────────────────────────────────── */}
      {puertos.in && (() => {
        const p = portPoint(n, "in");
        return (
          <g key="in">
            <circle className="df-port" cx={p.x} cy={p.y} fill="var(--surface)"
              stroke={clr} strokeWidth={1.5} pointerEvents="none" />
            <circle className="df-port-hit" cx={p.x} cy={p.y} r={12} />
          </g>
        );
      })()}

      {puertos.salidas.map(port => {
        const p = portPoint(n, port);
        const caliente = puertoCaliente === port;
        // La rama se dice en el PUERTO y con color: verde el si, rojo el no. Y
        // el circulito va RELLENO de su color, no solo con el borde, porque a
        // 4,5 px de radio un aro de 1,5 deja casi nada de color que ver, y
        // justo eso es ahora lo que lleva la informacion.
        const rama = colorRama(port);
        const borde = rama || (port === "resto" ? "#6b7280" : clr);
        return (
          <g key={port}>
            {/* El nombre de la rama, para quien no distinga el verde del rojo o
                navegue con lector de pantalla: el color no puede ser el unico
                sitio donde este el dato. */}
            {rama && <title>{port === "si" ? "Rama del si" : "Rama del no"}</title>}
            <circle className={"df-port" + (caliente ? " df-port-hot" : "")}
              cx={p.x} cy={p.y}
              fill={caliente ? "var(--accent)" : rama || "var(--surface)"}
              stroke={borde}
              strokeWidth={1.5} strokeDasharray={port === "resto" ? "2 2" : undefined}
              pointerEvents="none" />
            {/* Area sensible de 12 px: 4,5 no se aciertan, y menos con el dedo */}
            {editable && (
              <circle className="df-port-hit" cx={p.x} cy={p.y} r={12}
                onPointerDown={e => onPointerDownPuerto?.(e, n, port)} />
            )}
          </g>
        );
      })}
    </g>
  );
}
